<template>
  <AIVDialog
    :visible="value"
    @update:visible="$emit('input', $event)"
    :title="step === 'type' ? 'New Conversation' : step === 'direct' ? 'Select User' : 'Create Group'"
    width="500px"
  >
    <!-- Step 1: Choose conversation type -->
    <div v-if="step === 'type'" class="type-selection">
      <button class="type-option" @click="selectType('direct')">
        <div class="type-icon">
          <i class="fa-solid fa-user"></i>
        </div>
        <div class="type-info">
          <h4>Direct Message</h4>
          <p>Start a private conversation with one person</p>
        </div>
        <i class="fa-solid fa-chevron-right"></i>
      </button>

      <button class="type-option" @click="selectType('group')">
        <div class="type-icon">
          <i class="fa-solid fa-users"></i>
        </div>
        <div class="type-info">
          <h4>Group Conversation</h4>
          <p>Create a conversation with multiple people</p>
        </div>
        <i class="fa-solid fa-chevron-right"></i>
      </button>
    </div>

    <!-- Step 2a: Select user for direct message -->
    <div v-else-if="step === 'direct'" class="user-selection">
      <div class="search-container">
        <i class="fa-solid fa-search search-icon"></i>
        <input
          v-model="userSearch"
          type="text"
          class="search-input"
          placeholder="Search users..."
          @input="debounceSearch"
        />
      </div>

      <div class="users-list">
        <div v-if="loadingUsers" class="loading-state">
          <i class="fa-solid fa-spinner fa-spin"></i>
        </div>

        <div v-else-if="filteredUsers.length === 0" class="empty-state">
          <p>{{ userSearch ? 'No users found' : 'Start typing to search users' }}</p>
        </div>

        <div
          v-for="user in filteredUsers"
          :key="user.id"
          class="user-item"
          @click="selectDirectUser(user)"
        >
          <div class="user-avatar">
            <i class="fa-solid fa-user"></i>
          </div>
          <div class="user-info">
            <span class="user-name">{{ user.first_name }} {{ user.last_name }}</span>
            <span class="user-type">{{ user.user_type }}</span>
          </div>
        </div>
      </div>

      <div class="modal-actions">
        <el-button @click="goBack">Back</el-button>
      </div>
    </div>

    <!-- Step 2b: Create group conversation -->
    <div v-else-if="step === 'group'" class="group-creation">
      <div class="form-group">
        <label>Group Name</label>
        <input
          v-model="groupName"
          type="text"
          class="form-input"
          placeholder="Enter group name..."
        />
      </div>

      <div class="form-group">
        <label>Select Participants</label>
        <div class="search-container">
          <i class="fa-solid fa-search search-icon"></i>
          <input
            v-model="userSearch"
            type="text"
            class="search-input"
            placeholder="Search users..."
            @input="debounceSearch"
          />
        </div>

        <div class="selected-users" v-if="selectedUsers.length > 0">
          <div
            v-for="user in selectedUsers"
            :key="user.id"
            class="selected-user-chip"
          >
            {{ user.first_name }} {{ user.last_name }}
            <button class="remove-user" @click="removeSelectedUser(user)">
              <i class="fa-solid fa-times"></i>
            </button>
          </div>
        </div>

        <div class="users-list">
          <div v-if="loadingUsers" class="loading-state">
            <i class="fa-solid fa-spinner fa-spin"></i>
          </div>

          <div
            v-for="user in availableUsers"
            :key="user.id"
            :class="['user-item', { selected: isUserSelected(user) }]"
            @click="toggleUserSelection(user)"
          >
            <div class="user-avatar">
              <i class="fa-solid fa-user"></i>
            </div>
            <div class="user-info">
              <span class="user-name">{{ user.first_name }} {{ user.last_name }}</span>
              <span class="user-type">{{ user.user_type }}</span>
            </div>
            <i
              v-if="isUserSelected(user)"
              class="fa-solid fa-check selected-check"
            ></i>
          </div>
        </div>
      </div>

      <div class="modal-actions">
        <el-button @click="goBack">Back</el-button>
        <el-button
          type="primary"
          :disabled="!canCreateGroup"
          :loading="creating"
          @click="createGroup"
        >
          Create Group
        </el-button>
      </div>
    </div>
  </AIVDialog>
</template>

<script>
import { usersService } from "@/serviceHandlers/usersService";
import { useMessagesStore } from "@/stores/messagesStore";
import { useLoginStore } from "@/stores/loginStore";

export default {
  name: "NewConversationModal",
  props: {
    value: {
      type: Boolean,
      default: false,
    },
  },
  data() {
    return {
      step: "type",
      userSearch: "",
      users: [],
      selectedUsers: [],
      groupName: "",
      loadingUsers: false,
      creating: false,
      searchTimeout: null,
      messagesStore: useMessagesStore(),
      loginStore: useLoginStore(),
    };
  },
  computed: {
    filteredUsers() {
      const currentUserId = this.loginStore.user?.id;
      return this.users.filter((user) => user.id !== currentUserId);
    },

    availableUsers() {
      const currentUserId = this.loginStore.user?.id;
      const selectedIds = new Set(this.selectedUsers.map((u) => u.id));
      return this.users.filter(
        (user) => user.id !== currentUserId && !selectedIds.has(user.id)
      );
    },

    canCreateGroup() {
      return this.groupName.trim() && this.selectedUsers.length >= 1;
    },
  },
  watch: {
    value(newVal) {
      if (newVal) {
        this.reset();
      }
    },
  },
  methods: {
    reset() {
      this.step = "type";
      this.userSearch = "";
      this.users = [];
      this.selectedUsers = [];
      this.groupName = "";
    },

    selectType(type) {
      this.step = type;
      if (type === "direct" || type === "group") {
        this.loadUsers();
      }
    },

    goBack() {
      this.step = "type";
      this.userSearch = "";
      this.selectedUsers = [];
      this.groupName = "";
    },

    async loadUsers() {
      this.loadingUsers = true;
      try {
        const response = await usersService.list({ activeStatus: "active" });
        this.users = response || [];
      } catch {
        this.users = [];
      } finally {
        this.loadingUsers = false;
      }
    },

    debounceSearch() {
      clearTimeout(this.searchTimeout);
      this.searchTimeout = setTimeout(() => {
        this.searchUsers();
      }, 300);
    },

    async searchUsers() {
      if (!this.userSearch.trim()) {
        this.loadUsers();
        return;
      }

      this.loadingUsers = true;
      try {
        // Filter users locally based on search
        const query = this.userSearch.toLowerCase();
        const response = await usersService.list({ activeStatus: "active" });
        this.users = (response || []).filter((user) => {
          const fullName = `${user.first_name} ${user.last_name}`.toLowerCase();
          const email = (user.email || "").toLowerCase();
          return fullName.includes(query) || email.includes(query);
        });
      } catch {
      } finally {
        this.loadingUsers = false;
      }
    },

    async selectDirectUser(user) {
      this.creating = true;
      try {
        const conversation = await this.messagesStore.getOrCreateDirectConversation(
          user.id
        );
        this.$emit("created", conversation);
        this.$emit("input", false);
      } catch (error) {
        this.$notifyError("Failed to create conversation");
      } finally {
        this.creating = false;
      }
    },

    isUserSelected(user) {
      return this.selectedUsers.some((u) => u.id === user.id);
    },

    toggleUserSelection(user) {
      if (this.isUserSelected(user)) {
        this.removeSelectedUser(user);
      } else {
        this.selectedUsers.push(user);
      }
    },

    removeSelectedUser(user) {
      this.selectedUsers = this.selectedUsers.filter((u) => u.id !== user.id);
    },

    async createGroup() {
      if (!this.canCreateGroup) return;

      this.creating = true;
      try {
        const participantIds = this.selectedUsers.map((u) => u.id);
        const conversation = await this.messagesStore.createConversation({
          name: this.groupName.trim(),
          type: "group",
          participantIds,
        });
        this.$emit("created", conversation);
        this.$emit("input", false);
      } catch (error) {
        this.$notifyError("Failed to create group");
      } finally {
        this.creating = false;
      }
    },
  },
};
</script>

<style scoped>
.type-selection {
  display: flex;
  flex-direction: column;
  gap: 12px;
}

.type-option {
  display: flex;
  align-items: center;
  gap: 16px;
  padding: 16px;
  background: rgba(255, 255, 255, 0.05);
  border: 1px solid rgba(255, 255, 255, 0.1);
  border-radius: 8px;
  cursor: pointer;
  transition: all 0.2s;
  text-align: left;
  width: 100%;
}

.type-option:hover {
  background: rgba(255, 255, 255, 0.1);
  border-color: #6BA6CD;
}

.type-icon {
  width: 48px;
  height: 48px;
  border-radius: 50%;
  background: linear-gradient(135deg, #6BA6CD, #4a8ab8);
  display: flex;
  align-items: center;
  justify-content: center;
  color: #fff;
  font-size: 20px;
  flex-shrink: 0;
}

.type-info {
  flex: 1;
}

.type-info h4 {
  margin: 0 0 4px 0;
  font-size: 15px;
  font-weight: 600;
  color: #fff;
}

.type-info p {
  margin: 0;
  font-size: 13px;
  color: rgba(255, 255, 255, 0.6);
}

.type-option > .fa-chevron-right {
  color: rgba(255, 255, 255, 0.4);
}

.user-selection,
.group-creation {
  display: flex;
  flex-direction: column;
  gap: 16px;
}

.search-container {
  position: relative;
}

.search-icon {
  position: absolute;
  left: 14px;
  top: 50%;
  transform: translateY(-50%);
  color: rgba(255, 255, 255, 0.4);
  font-size: 14px;
}

.search-input {
  width: 100%;
  padding: 12px 12px 12px 40px;
  border: 1px solid rgba(255, 255, 255, 0.15);
  border-radius: 8px;
  background: rgba(255, 255, 255, 0.05);
  color: #fff;
  font-size: 14px;
}

.search-input:focus {
  outline: none;
  border-color: #6BA6CD;
}

.search-input::placeholder {
  color: rgba(255, 255, 255, 0.4);
}

.users-list {
  max-height: 300px;
  overflow-y: auto;
  border: 1px solid rgba(255, 255, 255, 0.1);
  border-radius: 8px;
}

.loading-state,
.empty-state {
  padding: 24px;
  text-align: center;
  color: rgba(255, 255, 255, 0.5);
}

.user-item {
  display: flex;
  align-items: center;
  gap: 12px;
  padding: 12px 16px;
  cursor: pointer;
  transition: background-color 0.2s;
  border-bottom: 1px solid rgba(255, 255, 255, 0.05);
}

.user-item:last-child {
  border-bottom: none;
}

.user-item:hover {
  background: rgba(255, 255, 255, 0.05);
}

.user-item.selected {
  background: rgba(107, 166, 205, 0.15);
}

.user-avatar {
  width: 40px;
  height: 40px;
  border-radius: 50%;
  background: rgba(255, 255, 255, 0.1);
  display: flex;
  align-items: center;
  justify-content: center;
  color: rgba(255, 255, 255, 0.6);
  flex-shrink: 0;
}

.user-info {
  flex: 1;
  display: flex;
  flex-direction: column;
}

.user-name {
  font-weight: 500;
  color: #fff;
}

.user-type {
  font-size: 12px;
  color: rgba(255, 255, 255, 0.5);
}

.selected-check {
  color: #6BA6CD;
}

.form-group {
  display: flex;
  flex-direction: column;
  gap: 8px;
}

.form-group label {
  font-size: 14px;
  font-weight: 500;
  color: rgba(255, 255, 255, 0.8);
}

.form-input {
  padding: 12px 16px;
  border: 1px solid rgba(255, 255, 255, 0.15);
  border-radius: 8px;
  background: rgba(255, 255, 255, 0.05);
  color: #fff;
  font-size: 14px;
}

.form-input:focus {
  outline: none;
  border-color: #6BA6CD;
}

.form-input::placeholder {
  color: rgba(255, 255, 255, 0.4);
}

.selected-users {
  display: flex;
  flex-wrap: wrap;
  gap: 8px;
  margin-bottom: 8px;
}

.selected-user-chip {
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 6px 12px;
  background: rgba(107, 166, 205, 0.2);
  border-radius: 16px;
  font-size: 13px;
  color: #fff;
}

.remove-user {
  background: none;
  border: none;
  padding: 0;
  cursor: pointer;
  color: rgba(255, 255, 255, 0.6);
  font-size: 12px;
}

.remove-user:hover {
  color: #fff;
}

.modal-actions {
  display: flex;
  justify-content: flex-end;
  gap: 12px;
  margin-top: 8px;
  padding-top: 16px;
  border-top: 1px solid rgba(255, 255, 255, 0.1);
}

/* Scrollbar styling */
.users-list::-webkit-scrollbar {
  width: 6px;
}

.users-list::-webkit-scrollbar-track {
  background: transparent;
}

.users-list::-webkit-scrollbar-thumb {
  background: rgba(255, 255, 255, 0.15);
  border-radius: 3px;
}
</style>
